<template>
    <div class="content">
        <form @submit.prevent="signIn">
            <template v-if="!showCodeConfirmation">
                <div class="field">
                    <div class="control">
                        <masked-phone v-if="client && client.login.phoneMask"
                                      v-model="login"
                                      :mask="client.login.phoneMask"
                                      v-validate="{ required: true, length: (client.login.phoneMask.match(/\d/g) || []).length }"
                                      data-vv-name="login"
                                      data-vv-validate-on="input"
                                      :class="['input', 'is-large', {'is-danger': errors.has('login')}]"
                                      autocomplete="username"
                                      autofocus
                                      @focus="clearServerErrors()" />

                        <input v-else
                               v-model="login"
                               v-validate="`required|min:3|max:50`"
                               data-vv-name="login"
                               data-vv-validate-on="change"
                               @focus="clearServerErrors()"
                               :class="{'is-danger': errors.has('login')}"
                               class="input is-large"
                               type="text"
                               :placeholder="'components.pages.login.login password.login placeholder' | translate"
                               autofocus
                               maxlength="50"
                               autocomplete="username" />
                    </div>
                    <p class="help is-danger" v-if="errors.has('login:required')">{{"components.pages.login.validation.login required" | translate}}</p>
                    <p class="help is-danger" v-if="errors.has('login:min')">{{ $t("components.pages.login.validation.login min length", $validator.fields.find({ name: 'login' }).rules) }}</p>
                    <p class="help is-danger" v-if="errors.has('login:max')">{{ $t("components.pages.login.validation.login max length", $validator.fields.find({ name: 'login' }).rules) }}</p>
                </div>
                <div class="field">
                    <div class="control has-icons-right">
                        <input v-model="password"
                               v-validate="`required|min:4|max:20`"
                               data-vv-name="password"
                               data-vv-validate-on="change"
                               @focus="clearServerErrors()"
                               :class="{'is-danger': errors.has('password')}"
                               class="input is-large"
                               :type="showPassword ? 'text' : 'password'"
                               :placeholder="'components.pages.login.login password.password placeholder' | translate"
                               maxlength="20"
                               autocomplete="password"
                               ref="password" />

                        <a class="icon is-small is-right bng-clickable" @click="toggleShowPassword()">
                            <span v-show="!showPassword"><i class="far fa-eye-slash"></i></span>
                            <span v-show="showPassword"><i class="far fa-eye"></i></span>
                        </a>
                    </div>
                    <p class="help is-danger" v-if="errors.has('password:required')">{{"components.pages.login.validation.password required" | translate}}</p>
                    <p class="help is-danger" v-if="errors.has('password:min')">{{"components.pages.login.validation.password min length" | translate}}</p>
                    <p class="help is-danger" v-if="errors.has('password:max')">{{"components.pages.login.validation.password max length" | translate}}</p>
                    <p class="help is-danger" v-if="errors.has('login:incorrectLoginOrPassword', 'serverErrors')">{{"components.pages.login.validation.incorrect login or password" | translate}}</p>
                    <p class="help is-danger" v-if="errors.has('login:playerIsLocked', 'serverErrors')">{{"components.pages.login.validation.player is locked" | translate}}</p>
                    <p class="help is-danger has-text-weight-bold" v-if="failedAccessAttemptsLeft">{{"components.pages.login.validation.failed access warning" | translate}} {{failedAccessAttemptsLeft}}</p>
                </div>
            </template>
            <email-confirmation v-if="showCodeConfirmation" :value.sync="code" />
            <!-- language -->
            <div v-if="availableLanguages.length > 1" class="is-inline-block login-language-block">
                <div class="field has-addons">
                    <p class="control">
                        <a class="button is-static"><span class="icon"><i class="fas fa-globe"></i></span></a>
                    </p>
                    <div class="control">
                        <div class="select">
                            <select v-model="lang">
                                <option v-for="l in availableLanguages" :key="l" :value="l">{{langDescriptions[l]}}</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <!-- recaptcha -->
            <recaptcha v-show="false" ref="recaptcha" :on-response="response => { recaptchaResponse = response; }" />

            <!-- login button -->
            <div class="field">
                <button class="button button-login is-large is-link" :disabled="errors.any() || loading || !isRecaptchaVerified">
                    <span v-if="!loading">{{$t(showCodeConfirmation ? "common.confirm" : "components.pages.login.login")}}</span>
                    <span class="icon" v-if="loading"><i class="fas fa-circle-notch fa-pulse"></i></span>
                </button>
                <button v-if="showCodeConfirmation && !loading" class="button is-large" @click.prevent="() => { showCodeConfirmation = false; code = null; errors.clear('serverErrors'); }" :disabled="loading">
                    <span>{{"common.cancel" | translate}}</span>
                </button>
            </div>

            <!-- registration link -->
            <div class="bng-login-links-block">
                <div v-if="registrationAllowed" class="field">
                    <router-link :to="{name: 'registration'}" class="is-size-5">{{"components.pages.login.registration" | translate}}</router-link>
                </div>

                <!-- password restore link -->
                <div v-if="passwordRestoreAllowed" class="field">
                    <router-link :to="{name:'password-restore'}" class="is-size-5">{{"components.pages.login.restore password" | translate}}</router-link>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
    import Vue from 'vue';

    import Recaptcha from '../../../common/recaptcha/recaptcha';
    import MaskedPhone from '../../../common/phone-number/phone-number-masked.vue';
    import EmailConfirmation from "./email-confirmation.vue";

    import authService from '../../../../../../_common/services/authentication.service';

    export default {
        name: 'password-login',
        $_veeValidate: { validator: 'new' },
        components: { Recaptcha, MaskedPhone, EmailConfirmation },
        props: {
            success: {
                type: Function
            }
        },
        data() {
            return {
                showPassword: false,
                loading: false,
                login: '',
                password: '',
                code: null,
                recaptchaResponse: null,
                failedAccessAttemptsLeft: null,
                showCodeConfirmation: false
            };
        },
        computed: {
            client() {
                return Vue.state.client.get();
            },
            registrationAllowed() {
                return this.client && this.client.registration.registrationTypes && this.client.registration.registrationTypes.length;
            },
            passwordRestoreAllowed() {
                return this.client && this.client.view.auth.passwordRestoreAllowed;
            },
            lang: {
                get() {
                    return Vue.state.lang.currentLang;
                },
                set(val) {
                    Vue.state.lang.currentLang = val;
                }
            },
            availableLanguages() {
                return Vue.state.lang.availableLanguages;
            },
            langDescriptions() {
                return Vue.state.lang.langDescriptions;
            },
            isRecaptchaVerified() {
                return !this.$refs.recaptcha || this.$refs.recaptcha.verified;
            }
        },
        methods: {
            hide() {
                this.resetFields();
            },
            resetFields() {
                this.login = '';
                this.password = '';
                this.failedAccessAttemptsLeft = null;
            },
            async signIn() {

                await this.$validator.validateAll();

                if (this.errors.any() || this.loading || !this.isRecaptchaVerified || !this.login || !this.password) {
                    return;
                }

                this.loading = true;
                this.errors.clear();
                this.errors.clear("serverErrors");

                const loginResult = await authService.login({
                    login: this.$data.login,
                    password: this.$data.password,
                    recaptchaResponse: this.recaptchaResponse,
                    code: this.code
                });

                this.failedAccessAttemptsLeft = loginResult.failedAccessAttemptsLeft;

                if (loginResult.isSuccess) {
                    if (loginResult.codeSent) {
                        this.showCodeConfirmation = true;
                    }
                    else {
                        await this.play();
                    }
                } else {
                    this.errors.add({ field: 'login', rule: loginResult.error, msg: loginResult.error, scope: 'serverErrors' });
                    this.errors.add({ field: 'password', rule: loginResult.error, msg: loginResult.error, scope: 'serverErrors' });
                    this.errors.add({ field: 'confirmation code', rule: loginResult.error, msg: loginResult.error, scope: 'serverErrors' });
                }
                this.loading = false;
            },
            clearServerErrors() {
                this.errors.remove('login', 'serverErrors');
                this.errors.remove('password', 'serverErrors');
                this.errors.remove('confirmation code', 'serverErrors');
            },
            async play() {
                await Vue.state.player.reload();
                Vue.state.lang.ensureLocalLangOrPlayer(Vue.state.player.get().lang || 'ru');
                await Vue.router.teleport.home();
            },
            toggleShowPassword() {
                this.showPassword = !this.showPassword;
                setTimeout(() => this.$refs.password.focus());
            }
        },
        beforeDestroy() {
            this.resetFields();
        }
    };
</script>



// WEBPACK FOOTER //
// front/src/skins/bulmasite/components/pages/sitepages/login/password-login.vue